// csvValidation.ts - CSV format and security validation utilities

export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Detect CSV separator from a line.
 * Supports semicolon (default for German locale), comma, tab, and pipe.
 *
 * Precedence:
 *  - comma when present alone (no semicolon)
 *  - tab when present
 *  - pipe when present alone (no semicolon, no comma)
 *  - semicolon as default
 *
 * Kept in sync with `features/data-adjustments/utils/csvValidators.ts::detectCSVSeparator`.
 */
export function detectCSVSeparator(line: string): string {
  if (line.includes(',') && !line.includes(';')) return ',';
  if (line.includes('\t')) return '\t';
  if (line.includes('|') && !line.includes(';') && !line.includes(',')) return '|';
  return ';';
}

/**
 * Validate CSV format - check column count consistency
 * @param content - CSV content as string
 * @param options - Validation options
 */
export function validateCSVFormat(
  content: string,
  options: {
    minColumns?: number;
    maxColumns?: number;
    minLines?: number;
    checkLines?: number;
  } = {}
): ValidationResult {
  const {
    minColumns = 2,
    maxColumns,
    minLines = 2,
    checkLines = 100
  } = options;

  const lines = content.split('\n').filter(line => line.trim());

  if (lines.length === 0) {
    return { isValid: false, error: 'Empty CSV content' };
  }

  if (lines.length < minLines) {
    return {
      isValid: false,
      error: `CSV must have at least ${minLines} lines (header + data)`
    };
  }

  // Detect separator (semicolon, comma, tab, or pipe)
  const firstLine = lines[0];
  const separator = detectCSVSeparator(firstLine);

  // Check column count in first row (header)
  const expectedColumns = firstLine.split(separator).length;

  if (expectedColumns < minColumns) {
    return {
      isValid: false,
      error: `CSV must have at least ${minColumns} columns`
    };
  }

  // Check maximum columns if specified
  if (maxColumns !== undefined && expectedColumns > maxColumns) {
    return {
      isValid: false,
      error: `CSV has too many columns: ${expectedColumns}, expected maximum ${maxColumns}`
    };
  }

  // Check that all rows have same column count
  const linesToCheck = Math.min(lines.length, checkLines);
  for (let i = 1; i < linesToCheck; i++) {
    const columns = lines[i].split(separator).length;
    if (columns !== expectedColumns) {
      return {
        isValid: false,
        error: `CSV format inconsistent: Line ${i + 1} has ${columns} columns, expected ${expectedColumns}`
      };
    }
  }

  return { isValid: true };
}

/**
 * Check if string is in correct datetime format (YYYY-MM-DD HH:MM:SS)
 */
export function isValidDateTimeFormat(dateStr: string): boolean {
  const dateTimeRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;

  if (!dateTimeRegex.test(dateStr)) {
    return false;
  }

  try {
    const [datePart, timePart] = dateStr.split(' ');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute, second] = timePart.split(':').map(Number);

    // Check value ranges
    if (
      year < 1900 || year > 2100 ||
      month < 1 || month > 12 ||
      day < 1 || day > 31 ||
      hour < 0 || hour > 23 ||
      minute < 0 || minute > 59 ||
      second < 0 || second > 59
    ) {
      return false;
    }

    // Check date validity (e.g. February 30th)
    const date = new Date(year, month - 1, day, hour, minute, second);
    return date.getFullYear() === year &&
           date.getMonth() === month - 1 &&
           date.getDate() === day;
  } catch {
    return false;
  }
}

/**
 * Fix incomplete datetime format
 */
export function fixDateTimeFormat(dateStr: string): string {
  // If already correct format, return as-is
  if (isValidDateTimeFormat(dateStr)) {
    return dateStr;
  }

  // Year only (e.g. "2020")
  if (/^\d{4}$/.test(dateStr)) {
    return `${dateStr}-01-01 00:00:00`;
  }

  // Year and month (e.g. "2020-01")
  if (/^\d{4}-\d{1,2}$/.test(dateStr)) {
    return `${dateStr}-01 00:00:00`;
  }

  // Year, month and day without time (e.g. "2020-01-01")
  if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(dateStr)) {
    return `${dateStr} 00:00:00`;
  }

  // Date and partial time (e.g. "2020-01-01 12")
  if (/^\d{4}-\d{1,2}-\d{1,2} \d{1,2}$/.test(dateStr)) {
    return `${dateStr}:00:00`;
  }

  // Date and time without seconds (e.g. "2020-01-01 12:30")
  if (/^\d{4}-\d{1,2}-\d{1,2} \d{1,2}:\d{1,2}$/.test(dateStr)) {
    return `${dateStr}:00`;
  }

  // Cannot fix, return original
  return dateStr;
}

/**
 * Clean and normalize CSV content with ';' delimiter
 */
export function cleanCSVContent(fileContent: string): string {
  const lines = fileContent
    .split('\n')
    .map(line => line.trim())
    .filter(line => line !== '');

  const formattedLines = lines.map((line, index) => {
    const parts = line.split(';').map(part => part.trim());

    // If not header row, try to fix date format in first column
    if (index > 0 && parts.length > 0) {
      const firstColumn = parts[0];
      if (isValidDateTimeFormat(firstColumn) || /^\d{4}-\d{2}-\d{2}/.test(firstColumn)) {
        parts[0] = fixDateTimeFormat(firstColumn);
      }
    }

    return parts.join(';');
  });

  return formattedLines.join('\n');
}

/**
 * Ensure CSV has header row
 */
export function ensureHeaderRow(
  fileContent: string,
  defaultHeader: string = 'UTC;Value'
): string {
  if (!fileContent.trim()) return fileContent;

  const content = fileContent.trim();
  const lines = content.split('\n').filter(line => line.trim());

  if (lines.length === 0) return fileContent;

  // Check if first row is not a date (probably header)
  const firstLine = lines[0];
  const firstColumn = firstLine.split(';')[0];

  if (!isValidDateTimeFormat(firstColumn) && !/^\d{4}-\d{2}-\d{2}/.test(firstColumn)) {
    return content; // Already has header
  }

  // Add header
  return `${defaultHeader}\n${content}`;
}

/**
 * Security validation of CSV content - prevention of CSV injection attacks
 * Checks for formula injection, suspicious commands, script injection, and cell length limits
 * Note: Allows legitimate negative numbers (e.g., -100, -5.5) but blocks formulas
 *
 * @param content - CSV content as string
 * @param options - Validation options
 * @returns ValidationResult indicating if content is safe
 */
export function validateCSVSecurity(
  content: string,
  options: {
    maxCellLength?: number;
    checkLines?: number;
  } = {}
): ValidationResult {
  const {
    maxCellLength = 10000,
    checkLines = 1000
  } = options;

  const lines = content.split('\n').filter(line => line.trim());

  // Security patterns to detect CSV injection
  const suspiciousCommands = /\b(cmd|powershell|bash|exec|eval|system)\b/i;
  const scriptPattern = /<script|javascript:|data:text\/html/i;

  // Check first N lines for performance
  const linesToCheck = Math.min(lines.length, checkLines);

  for (let i = 0; i < linesToCheck; i++) {
    const line = lines[i];
    const separator = detectCSVSeparator(line);
    const cells = line.split(separator);

    for (const cell of cells) {
      const trimmedCell = cell.trim();

      // Skip empty cells
      if (!trimmedCell) continue;

      const firstChar = trimmedCell.charAt(0);

      // Always block = and @ (definite formula injection)
      if (firstChar === '=' || firstChar === '@') {
        return {
          isValid: false,
          error: `Security risk detected on line ${i + 1}: Potential formula injection (cell starts with '${firstChar}')`
        };
      }

      // For + and -, check if it's a valid number
      if (firstChar === '+' || firstChar === '-') {
        // Try to parse as number
        const numValue = parseFloat(trimmedCell);

        // If it's NaN or the string contains non-numeric characters after +/- (except dot, digits, and scientific notation)
        // then it's likely a formula injection attempt
        if (isNaN(numValue) || !/^[+-]?\d+\.?\d*([eE][+-]?\d+)?$/.test(trimmedCell)) {
          return {
            isValid: false,
            error: `Security risk detected on line ${i + 1}: Potential formula injection (cell starts with '${firstChar}' but is not a valid number)`
          };
        }
      }

      // Check for suspicious commands
      if (suspiciousCommands.test(trimmedCell)) {
        return {
          isValid: false,
          error: `Security risk detected on line ${i + 1}: Suspicious command detected`
        };
      }

      // Check for script injection
      if (scriptPattern.test(trimmedCell)) {
        return {
          isValid: false,
          error: `Security risk detected on line ${i + 1}: Script injection attempt`
        };
      }

      // Check cell length (prevent memory exhaustion)
      if (trimmedCell.length > maxCellLength) {
        return {
          isValid: false,
          error: `Security risk detected on line ${i + 1}: Cell content exceeds maximum allowed length (${maxCellLength} characters)`
        };
      }
    }
  }

  return { isValid: true };
}
